/**
 * Firebase Client SDK Configuration
 *
 * Initializes Firebase services for client-side use:
 * - Realtime Database: For storing dive tables, calculations, and user data
 * - Authentication: For user login/registration
 * - Storage: For storing user files, certificates, dive logs
 */

import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import { getDatabase, type Database } from 'firebase/database';
import { getAuth, type Auth } from 'firebase/auth';
import {
  getStorage,
  type FirebaseStorage,
} from 'firebase/storage';
import { initAnalytics } from './analytics';

// Use default Firebase auth domain on localhost, custom domain in production
const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const authDomain = isLocalhost
  ? `${import.meta.env.VITE_FIREBASE_PROJECT_ID}.firebaseapp.com`
  : import.meta.env.VITE_FIREBASE_AUTH_DOMAIN;

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
};

// Validate required environment variables
const requiredEnvVars = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_DATABASE_URL',
  'VITE_FIREBASE_STORAGE_BUCKET',
] as const;

for (const envVar of requiredEnvVars) {
  if (!import.meta.env[envVar] || import.meta.env[envVar] === 'placeholder') {
    console.error(
      `Missing or invalid Firebase configuration: ${envVar}. Please check your .env.local file.`
    );
  }
}

// Initialize Firebase (singleton pattern - only initialize once)
let app: FirebaseApp;
if (getApps().length === 0) {
  app = initializeApp(firebaseConfig);
} else {
  app = getApps()[0];
}

// Initialize Firebase services
export const db: Database = getDatabase(app);
export const auth: Auth = getAuth(app);
export const storage: FirebaseStorage = getStorage(app);

// Initialize Firebase Analytics
initAnalytics(app);

// Export the app instance for advanced use cases
export { app };

// Export a connection verification function
export async function verifyFirebaseConnection(): Promise<boolean> {
  try {
    // Check if we can access the database reference
    if (!db) {
      console.error('Firebase Database not initialized');
      return false;
    }

    // Check if auth is initialized
    if (!auth) {
      console.error('Firebase Auth not initialized');
      return false;
    }

    // Check if storage is initialized
    if (!storage) {
      console.error('Firebase Storage not initialized');
      return false;
    }

    console.log('✅ Firebase services initialized successfully');
    console.log('  - Database URL:', firebaseConfig.databaseURL);
    console.log('  - Auth Domain:', firebaseConfig.authDomain);
    console.log('  - Storage Bucket:', firebaseConfig.storageBucket);

    return true;
  } catch (error) {
    console.error('Firebase connection verification failed:', error);
    return false;
  }
}
